import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_HEADING,
  FONT_UI,
  SKIN_THEMES,
} from "../layouts/constants";

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("adminToken")
  );
}

function api(path) {
  const base = API_BASE_URL.replace(/\/$/, "");
  if (base.endsWith("/api") && path.startsWith("/api/")) {
    return `${base}${path.replace(/^\/api/, "")}`;
  }
  return `${base}${path}`;
}

function formatLocalDateTime(date) {
  const pad = (n) => String(n).padStart(2, "0");

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function formatISTDateTime(value) {
  if (!value) return "—";

  const cleanValue = String(value).replace("Z", "");
  const date = new Date(cleanValue);

  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("en-IN", { month: "short" });
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");

  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;

  return `${day} ${month} ${year}, ${hours}:${minutes} ${ampm}`;
}

function getAllocationKey(guard) {
  return String(
    guard.allocation_id ||
      guard.route_allocation_id ||
      guard.allocated_id ||
      `${guard.guard_id}-${guard.route_id}-${guard.allocated_start_time}-${guard.allocated_end_time}`
  );
}

const MIN_RANDOM_ROUTE_MINUTES = 120;

function getRouteDurationMinutes(startTime, endTime) {
  const start = new Date(String(startTime).replace("Z", ""));
  const end = new Date(String(endTime).replace("Z", ""));

  if (
    Number.isNaN(start.getTime()) ||
    Number.isNaN(end.getTime()) ||
    start >= end
  ) {
    return 0;
  }

  return Math.floor((end.getTime() - start.getTime()) / (1000 * 60));
}

export default function InCampusPage() {
  const navigate = useNavigate();
  const skin = useSkin();

  const [guards, setGuards] = useState([]);
  const [checkType, setCheckType] = useState("random");
  const [selectedAllocationIds, setSelectedAllocationIds] = useState([]);
  const [scheduleTimes, setScheduleTimes] = useState({});
  const [generatedChecks, setGeneratedChecks] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  useEffect(() => {
    const token = getToken();
    const isLoggedIn = localStorage.getItem("isLoggedIn");

    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }

    fetchPageData();
  }, []);

  const routePage = useMemo(
    () =>
      dashboardData.user_map === "google"
        ? "/route-master"
        : "/route-master1",
    [dashboardData.user_map]
  );

  const fetchPageData = async () => {
    try {
      setLoading(true);
      const token = getToken();

      const [dashboardRes, guardsRes] = await Promise.all([
        fetch(api("/api/dashboard/admin/dashboard"), {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }),
        fetch(api("/api/incampus/today-route-allocated-guards"), {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }),
      ]);

      if (dashboardRes.ok) {
        const dashboardJson = await dashboardRes.json();
        const companyDetails = dashboardJson?.data?.companyDetails || {};
        setDashboardData((prev) => ({ ...prev, ...companyDetails }));
      }

      if (!guardsRes.ok) {
        throw new Error("Failed to load today allocated guard list");
      }

      const guardsJson = await guardsRes.json();
      const list = guardsJson?.data || guardsJson || [];

      setGuards(list);
      toast.success(`Loaded ${list.length} guards`);
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to load In Campus Check page.");
    } finally {
      setLoading(false);
    }
  };

  const toggleGuard = (allocationKey) => {
    setSelectedAllocationIds((prev) =>
      prev.includes(allocationKey)
        ? prev.filter((id) => id !== allocationKey)
        : [...prev, allocationKey]
    );
    setGeneratedChecks([]);
  };

  const selectAll = () => {
    const allKeys = guards.map((g) => getAllocationKey(g));
    setSelectedAllocationIds(allKeys);
    setGeneratedChecks([]);
  };

  const deselectAll = () => {
    setSelectedAllocationIds([]);
    setGeneratedChecks([]);
  };

  const generateRandomTimes = (startTime, endTime) => {
    const start = new Date(String(startTime).replace("Z", ""));
    const end = new Date(String(endTime).replace("Z", ""));

    if (
      Number.isNaN(start.getTime()) ||
      Number.isNaN(end.getTime()) ||
      start >= end
    ) {
      return [];
    }

    const durationMinutes = getRouteDurationMinutes(startTime, endTime);

    if (durationMinutes < MIN_RANDOM_ROUTE_MINUTES) {
      return [];
    }

    const times = [];
    const current = new Date(start);

    while (current < end) {
      const randomMinutes = Math.floor(Math.random() * 60);
      const checkTime = new Date(current);
      checkTime.setMinutes(checkTime.getMinutes() + randomMinutes);

      if (checkTime < end) {
        times.push(formatLocalDateTime(checkTime));
      }

      current.setHours(current.getHours() + 2);
    }

    return times;
  };

  const handleGenerateChecks = () => {
    if (!selectedAllocationIds.length) {
      toast.error("Please select at least one allocation.");
      return;
    }

    const checks = [];

    for (const allocationKey of selectedAllocationIds) {
      const guard = guards.find((g) => getAllocationKey(g) === allocationKey);
      if (!guard) continue;

      if (checkType === "random") {
        const durationMinutes = getRouteDurationMinutes(
          guard.allocated_start_time,
          guard.allocated_end_time
        );

        if (durationMinutes < MIN_RANDOM_ROUTE_MINUTES) {
          setGeneratedChecks([]);
          toast.error("This route is too small. Please try Schedule.");
          return;
        }

        const randomTimes = generateRandomTimes(
          guard.allocated_start_time,
          guard.allocated_end_time
        );

        randomTimes.forEach((time) => {
          checks.push({
            allocation_id:
              guard.allocation_id ||
              guard.route_allocation_id ||
              guard.allocated_id,
            guard_id: guard.guard_id,
            guard_name: guard.guard_name,
            route_id: guard.route_id,
            route_name: guard.route_name,
            check_type: "random",
            check_time: time,
          });
        });
      }

      if (checkType === "schedule") {
        const selectedTime = scheduleTimes[allocationKey];

        if (!selectedTime) {
          toast.error(`Please select schedule time for ${guard.guard_name}`);
          return;
        }

        checks.push({
          allocation_id:
            guard.allocation_id ||
            guard.route_allocation_id ||
            guard.allocated_id,
          guard_id: guard.guard_id,
          guard_name: guard.guard_name,
          route_id: guard.route_id,
          route_name: guard.route_name,
          check_type: "schedule",
          check_time: selectedTime,
        });
      }
    }

    if (!checks.length) {
      toast.error("No valid check time generated.");
      return;
    }

    console.table(checks);

    setGeneratedChecks(checks);
    toast.success(`${checks.length} check time(s) generated.`);
  };

  const handleSubmit = async () => {
    if (!generatedChecks.length) {
      toast.error("Please generate check times first.");
      return;
    }

    try {
      setSaving(true);
      const token = getToken();

      const res = await fetch(api("/api/incampus/in-campus-checks"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ checks: generatedChecks }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to save checks");
      }

      toast.success("In Campus checks saved successfully.");
      setSelectedAllocationIds([]);
      setScheduleTimes({});
      setGeneratedChecks([]);
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to save In Campus checks.");
    } finally {
      setSaving(false);
    }
  };

  const handleViewList = () => {
    navigate("/incampus_list");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading in-campus checks...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="in-campus"
    >
      <Toaster position="top-right" />

      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-[22px]">
          <div>
            <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
              In Campus Check
            </h1>
            <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
              Today route allocated guard list
            </p>
          </div>

          <button
            type="button"
            onClick={handleViewList}
            className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer"
            style={{
              background: "var(--btn-bg)",
              color: "var(--btn-text)",
            }}
          >
            View List
          </button>
        </div>

        <div className="relative overflow-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 mb-[18px]">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--accent)]" />
          <div className="flex items-center justify-between gap-3.5 flex-wrap">
            <div>
              <h2 className="text-[18px] font-extrabold m-0">
                In Campus Checking
              </h2>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Generate random or scheduled guard check times for campus routes
              </p>
            </div>

            <div className="flex gap-3 flex-wrap items-center">
              <StatCard label="Guards" value={guards.length} color="blue" />
              <StatCard
                label="Selected"
                value={selectedAllocationIds.length}
                color="green"
              />
              <StatCard
                label="Generated"
                value={generatedChecks.length}
                color="amber"
              />
            </div>
          </div>
        </div>

        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 mb-[18px]">
          <div className="flex items-center justify-between gap-3.5 flex-wrap mb-4">
            <div>
              <h3 className="text-[18px] font-extrabold m-0">
                Allocation List
              </h3>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Select guards and generate in-campus checking times.
              </p>
            </div>

            <div className="flex gap-1.5 bg-[var(--surface)] p-1.5 rounded-full">
              <button
                type="button"
                onClick={() => {
                  setCheckType("random");
                  setGeneratedChecks([]);
                }}
                className="px-4 py-2 rounded-full text-[12px] font-extrabold"
                style={{
                  background:
                    checkType === "random" ? "var(--btn-bg)" : "transparent",
                  color:
                    checkType === "random" ? "white" : "var(--text-muted)",
                }}
              >
                Random
              </button>

              <button
                type="button"
                onClick={() => {
                  setCheckType("schedule");
                  setGeneratedChecks([]);
                }}
                className="px-4 py-2 rounded-full text-[12px] font-extrabold"
                style={{
                  background:
                    checkType === "schedule" ? "var(--btn-bg)" : "transparent",
                  color:
                    checkType === "schedule" ? "white" : "var(--text-muted)",
                }}
              >
                Schedule
              </button>
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-[var(--surface-border)]">
            <table className="w-full min-w-[700px] text-[13px]">
              <thead>
                <tr style={{ background: "var(--btn-bg)" }}>
                  <th className="px-4 py-3 text-left text-white">Select</th>
                  <th className="px-4 py-3 text-left text-white">Guard</th>
                  <th className="px-4 py-3 text-left text-white">Route</th>
                  <th className="px-4 py-3 text-left text-white">
                    Start Time
                  </th>
                  <th className="px-4 py-3 text-left text-white">End Time</th>
                  {checkType === "schedule" && (
                    <th className="px-4 py-3 text-left text-white">
                      Check Time
                    </th>
                  )}
                </tr>
              </thead>

              <tbody>
                {guards.length === 0 ? (
                  <tr>
                    <td
                      colSpan={checkType === "schedule" ? 6 : 5}
                      className="text-center py-8 text-[var(--text-muted)]"
                    >
                      No allocated guards found today.
                    </td>
                  </tr>
                ) : (
                  guards.map((guard) => {
                    const allocationKey = getAllocationKey(guard);
                    const isSelected =
                      selectedAllocationIds.includes(allocationKey);

                    return (
                      <tr
                        key={allocationKey}
                        className="border-b border-[var(--divider)]"
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleGuard(allocationKey)}
                            className="w-4 h-4"
                          />
                        </td>

                        <td className="px-4 py-3">
                          <div className="font-extrabold">
                            {guard.guard_name}
                          </div>
                          <div className="text-[12px] text-[var(--text-muted)]">
                            ID: {guard.guard_id}
                          </div>
                        </td>

                        <td className="px-4 py-3">
                          <div>{guard.route_name}</div>
                          <div className="text-[12px] text-[var(--text-muted)]">
                            Route ID: {guard.route_id}
                          </div>
                        </td>

                        <td className="px-4 py-3">
                          {formatISTDateTime(guard.allocated_start_time)}
                        </td>

                        <td className="px-4 py-3">
                          {formatISTDateTime(guard.allocated_end_time)}
                        </td>

                        {checkType === "schedule" && (
                          <td className="px-4 py-3">
                            <input
                              type="datetime-local"
                              value={scheduleTimes[allocationKey] || ""}
                              onChange={(e) =>
                                setScheduleTimes((prev) => ({
                                  ...prev,
                                  [allocationKey]: e.target.value,
                                }))
                              }
                              className="w-full rounded-xl border px-3.5 py-2.5"
                            />
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
            <div className="flex gap-2">
              <button type="button" onClick={selectAll}>
                Select All
              </button>
              <button type="button" onClick={deselectAll}>
                Deselect All
              </button>
            </div>

            <button
              type="button"
              onClick={handleGenerateChecks}
              disabled={!selectedAllocationIds.length}
              style={{
                background: "var(--btn-bg)",
                color: "var(--btn-text)",
              }}
              className="border-0 rounded-xl px-5 py-2.5 text-[13px] font-extrabold"
            >
              Generate Check Time
            </button>
          </div>
        </div>

        {generatedChecks.length > 0 && (
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6">
            <div className="flex items-center justify-between gap-3.5 flex-wrap mb-4">
              <div>
                <h3 className="text-[18px] font-extrabold m-0">
                  Generated Checks
                </h3>
                <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                  Save these generated in-campus check records to database.
                </p>
              </div>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                style={{ background: "#22c55e", color: "white" }}
                className="border-0 rounded-xl px-5 py-2.5 text-[13px] font-extrabold"
              >
                {saving ? "Saving..." : "Save Checks"}
              </button>
            </div>

            <div className="overflow-x-auto rounded-xl border border-[var(--surface-border)]">
              <table className="w-full min-w-[500px] text-[13px]">
                <thead>
                  <tr style={{ background: "var(--btn-bg)" }}>
                    <th className="px-4 py-3 text-left text-white">Guard</th>
                    <th className="px-4 py-3 text-left text-white">Route</th>
                    <th className="px-4 py-3 text-left text-white">Type</th>
                    <th className="px-4 py-3 text-left text-white">
                      Check Time
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {generatedChecks.map((check, index) => (
                    <tr
                      key={`${check.allocation_id || check.guard_id}-${
                        check.check_time
                      }-${index}`}
                      className="border-b border-[var(--divider)]"
                    >
                      <td className="px-4 py-3">
                        <div className="font-extrabold">
                          {check.guard_name}
                        </div>
                        <div className="text-[12px] text-[var(--text-muted)]">
                          ID: {check.guard_id}
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <div>{check.route_name}</div>
                        <div className="text-[12px] text-[var(--text-muted)]">
                          Route ID: {check.route_id}
                        </div>
                      </td>

                      <td className="px-4 py-3">{check.check_type}</td>

                      <td className="px-4 py-3">
                        {formatISTDateTime(check.check_time)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function StatCard({ label, value, color }) {
  const colorMap = {
    blue: "bg-[rgba(59,130,246,0.12)] text-[#3b82f6]",
    green: "bg-[rgba(16,185,129,0.12)] text-[#10b981]",
    amber: "bg-[rgba(245,158,11,0.12)] text-[#f59e0b]",
    red: "bg-[rgba(239,68,68,0.12)] text-[#ef4444]",
    purple: "bg-[rgba(139,92,246,0.12)] text-[#8b5cf6]",
  };

  return (
    <div className={`rounded-xl px-3 py-2 border ${colorMap[color] || colorMap.blue}`}>
      <p
        className="text-[9px] font-extrabold uppercase tracking-wider opacity-75 m-0"
        style={{ fontFamily: FONT_UI }}
      >
        {label}
      </p>
      <p
        className="text-[18px] font-extrabold m-0 mt-0.5"
        style={{ fontFamily: FONT_HEADING }}
      >
        {value}
      </p>
    </div>
  );
}